import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { useParams, Link } from "react-router-dom";
import { fetchJournalPost } from "@/lib/api";
import Seo from "@/components/Seo";
import RichText from "@/components/RichText";
import ReadingProgress from "@/components/ReadingProgress";
import { ArrowLeft, Clock, Share2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s.slice(0, 10)).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return s.slice(0, 10); }
};

export default function JournalPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { fetchJournalPost(slug).then(setPost).catch(() => setErr(true)); }, [slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: post.title, url });
      else { await navigator.clipboard.writeText(url); toast("Link copied", { description: post.title }); }
    } catch { /* cancelled */ }
  };

  if (err) return <div className="mx-auto max-w-2xl px-5 py-24 text-center text-ink-2"><T>This article could not be found.</T> <Link to="/patrika" className="text-saffron"><T>Back to the Patrika</T></Link>.</div>;
  if (!post) return <p className="py-24 text-center text-ink-3">…</p>;

  return (
    <>
      <Seo title={post.title} description={post.excerpt} image={post.cover_image} type="article" path={`/patrika/${slug}`} />
      <ReadingProgress />
      <article className="pb-24" data-testid="journal-post">
        {/* Editorial header */}
        <header className="mx-auto max-w-3xl px-5 pt-12 md:px-8 md:pt-16">
          <Link to="/patrika" className="inline-flex items-center gap-1.5 text-sm text-ink-2 transition-colors hover:text-saffron"><ArrowLeft className="h-4 w-4" /> <T>The Patrika</T></Link>
          {post.deva_title && <p className="mt-8 font-deva text-2xl text-saffron deva-lh">{post.deva_title}</p>}
          <h1 className="mt-2 font-serif-latin text-4xl leading-[1.1] text-ink md:text-5xl">{post.title}</h1>
          {post.excerpt && <p className="mt-5 max-w-2xl font-serif-latin text-xl italic leading-relaxed text-ink-2">{post.excerpt}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 border-y border-line py-4 text-sm text-ink-3">
            {post.author && <span className="uppercase tracking-[0.1em] text-ink-2">{post.author}</span>}
            {post.published_at && <><span className="text-line">·</span><span>{fmtDate(post.published_at)}</span></>}
            {post.read_time && <><span className="text-line">·</span><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.read_time}</span></>}
            <button onClick={share} data-testid="journal-share" className="ml-auto inline-flex items-center gap-1.5 text-ink-2 transition-colors hover:text-saffron"><Share2 className="h-4 w-4" /> <T>Share</T></button>
          </div>
        </header>

        {post.cover_image && (
          <figure className="mx-auto mt-10 max-w-4xl px-5 md:px-8">
            <div className="overflow-hidden border border-line">
              <img src={post.cover_image} alt={post.title} className="w-full object-cover" />
            </div>
          </figure>
        )}

        {/* Body with drop cap on the first paragraph */}
        <div className="mx-auto mt-10 max-w-3xl px-5 md:px-8 md:mt-14">
          <RichText
            value={post.body}
            className="reading-content journal-body max-w-[68ch] font-serif-latin text-[18px] leading-[1.95] text-ink md:text-xl"
          />
          {post.tags?.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-2 border-t border-line pt-6">
              {post.tags.map((t) => (
                <span key={t} className="rounded-full border border-line px-3 py-1 text-xs text-ink-3">#{t}</span>
              ))}
            </div>
          )}
          <div className="mt-10">
            <Link to="/patrika" className="inline-flex items-center gap-1.5 text-sm text-saffron transition-transform hover:-translate-x-0.5"><ArrowLeft className="h-4 w-4" /> <T>More from the Patrika</T></Link>
          </div>
        </div>
      </article>
    </>
  );
}
